import { Component, OnInit, Input } from '@angular/core';
import { SpAttachmentLibraryService } from './sp-attachment-library.service';
import { Attachment } from '../models/attachment';
import { fileToBlob } from '../shared/file-util';

// Third Party
import { AttachmentFileInfo, Field } from '@pnp/sp';
import * as fa from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';

@Component({
    selector: 'sp-attachment',
    templateUrl: './sp-attachment-library.component.html',
    styleUrls: ['./sp-attachment-library.component.scss']
})
export class SpAttachmentLibraryComponent implements OnInit {

    private _listName: string;
    private _itemId: number;

    private _selectedUploadAttachment: AttachmentFileInfo;

    attachments: Attachment[] = [];
    unUpLoadedItems = 0;
    fa = fa;

    deleteSwalOptions = {
        title: 'Do you really want to delete this attachment?',
        type: 'warning',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'No, keep it',
        showCancelButton: 'true',
        customClass: {
            confirmButton: 'btn btn-success',
            cancelButton: 'btn btn-danger ml-sm-2'
        }
    };

    deleteAllSwalOptions = {
        title: 'Do you really want to delete all selected attachments?',
        message: 'All selected attachments added to this list item will be deleted.',
        type: 'warning',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'No, keep it',
        showCancelButton: 'true',
        customClass: {
            confirmButton: 'btn btn-danger',
            cancelButton: 'btn btn-success ml-sm-2'
        }
    };

    @Input()
    public set listName(listName: string) {
        this._listName = listName;
    }

    @Input()
    public set itemId(itemId: number) {
        this._itemId = itemId;
    }


    constructor(private spAttachmentLibraryService: SpAttachmentLibraryService) { }

    ngOnInit() {
        if (this._listName && this._itemId) {
            this.loadExistingFiles();
        }
    }

    loadExistingFiles() {
        const unuploaded = this.attachments.filter(x => !x.isSelected && x.serverRelativeUrl === null);
        this.spAttachmentLibraryService.getAttachmentsById(this._listName, this._itemId).then((data) => {
            this.attachments = [];
            data.result.forEach(element => {
                this.attachments.push({
                    fileName: element.FileName,
                    content: null,
                    extension: element.FileName.substring(element.FileName.lastIndexOf('.') + 1),
                    serverRelativeUrl: element.ServerRelativeUrl,
                    isUploaded: false,
                    isContentChanged: false,
                    isLoading: false,
                    isSelected: false,
                });
            });
            this.attachments.push(...unuploaded);
        });
    }

    onAttachmentAdded(event: any) {
        for (const file of event.target.files) {
            if (this.attachments.findIndex(x => x.fileName === file.name) < 0) {
                this.getBase64(file)
                    .then((response) => {
                        this.attachments.push({
                            fileName: file.name as string,
                            content: response as string,
                            extension: file.name.substring(file.name.lastIndexOf('.') + 1),
                            serverRelativeUrl: null,
                            isUploaded: false,
                            isContentChanged: false,
                            isLoading: false,
                            isSelected: false,
                        });
                    });
            } else {
                this.promptReplace(file);
            }
        }
    }

    promptReplace(file: any) {
        const replaceContentSwal = Swal.mixin({
            customClass: {
                confirmButton: 'btn btn-success',
                cancelButton: 'btn btn-danger ml-sm-2'
            },
            buttonsStyling: false,
        });

        replaceContentSwal.fire({
            title: 'File already exists. Do you want to replace it?',
            text: 'The file content will be replaced',
            type: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, replace it!',
            cancelButtonText: 'No, keep it'
        }).then((result) => {
            if (result.value) {
                this.getBase64(file)
                    .then((response) => {
                        const exitingFileIndex = this.attachments.findIndex(x => x.fileName === file.name);
                        const existingItem = this.attachments.find(x => x.fileName === file.name);
                        existingItem.content = response as string;
                        existingItem.extension = file.name.substring(file.name.lastIndexOf('.') + 1);
                        existingItem.isContentChanged = true;
                        existingItem.isUploaded = false;
                        this.attachments.splice(exitingFileIndex, 1, existingItem);
                    });
            } else if (result.dismiss === Swal.DismissReason.cancel) {
                Swal.fire(
                    'Cancelled',
                )
            }
        });
    }

    resetFiles(event: any) {
        event.target.value = null;
    }

    removeAttachment(file: Attachment) {
        file.isLoading = true;
        if (!file.serverRelativeUrl) {
            const index = this.attachments.findIndex(x => x.fileName === file.fileName);
            file.isLoading = false;
            this.attachments.splice(index, 1);
        } else {
            file.isLoading = true;
            this.spAttachmentLibraryService.deleteAttachment(this._listName, this._itemId, file.fileName).then((data) => {
                const index = this.attachments.findIndex(x => x.fileName === file.fileName);
                file.isLoading = false;
                this.attachments.splice(index, 1);
            }).catch((error) => {
                console.log(error);
            });
        }
    }

    uploadAttachment(file: Attachment) {
        file.isLoading = true;
        this._selectedUploadAttachment = {
            content : fileToBlob(file),
            name: file.fileName
        };
        this.spAttachmentLibraryService.addAttachment(this._listName, this._itemId, this._selectedUploadAttachment).then((data) => {
            file.isLoading = false;
            const fileIndex = this.attachments.findIndex(x => x.fileName === file.fileName);
            this.attachments[fileIndex].serverRelativeUrl = data.result.data.ServerRelativeUrl;
            this.attachments[fileIndex].isContentChanged = false;
        }).catch((error) => {
            console.log(error);
        });
    }

    hasNewUnUploadedAndSelcted(): boolean {
        return this.attachments.filter(x => x.serverRelativeUrl === null && x.isSelected).length > 0;
    }

    getUnUploadedCount(): number {
        return this.attachments.filter(x => x.serverRelativeUrl === null && x.isSelected).length;
    }

    hasSelected(): boolean {
        return this.attachments.filter(x => x.isSelected).length > 0;
    }

    allSelected(): boolean {
        return this.attachments.every(x => x.isSelected);
    }

    getSelectedCount(): number {
        return this.attachments.filter(x => x.isSelected).length;
    }

    markOrUnMarkAll() {

        this.attachments.forEach(e => {
            e.isSelected = e.isSelected ? false : true;
        });
    }

    uploadNewAttachments() {
        const newAttachments = Array<AttachmentFileInfo>();

        const newlyAddedAttachments = this.attachments.filter(x => x.serverRelativeUrl === null && x.isSelected);
        newlyAddedAttachments.forEach(x => {
            x.isLoading = true;
        });

        const promises = [];

        for (const attachment of newlyAddedAttachments) {
            promises.push(fileToBlob(attachment));
        }

        Promise.all(promises).then(attachmentBlobs => {
            attachmentBlobs.forEach(async (blob, index) => {
                await newAttachments.push({
                    name: (newlyAddedAttachments[index].fileName),
                    content: blob
                });
            });

            this.spAttachmentLibraryService.addAttachments(this._listName, this._itemId, newAttachments).then((data) => {
                this.loadExistingFiles();
            }).catch((error) => {
                console.log(error);
            });
        }).catch((error) => {
            console.log(error);
        });
    }

    replaceAttachment(file: Attachment) {
        file.isLoading = true;
        this.spAttachmentLibraryService.deleteAttachment(this._listName, this._itemId, file.fileName).then((data) => {
            file.isLoading = false;
            this.uploadAttachment(file);
        }).catch((error) => {
            console.log(error);
        });
    }

    deleteSelected() {
        const selectedLocalAttachments  = this.attachments.filter(x => x.isSelected && x.serverRelativeUrl === null);
        this.attachments.forEach(e => {
            e.isLoading = e.isSelected ? true : false;
        });
        selectedLocalAttachments.forEach(e => {
            const index = this.attachments.findIndex(a => a.fileName === e.fileName);
            this.attachments.splice(index, 1);
        });
        const serverFiles = this.attachments.filter(x => x.serverRelativeUrl !== null && x.isSelected);
        this.attachments.forEach(e => {
            e.isLoading = e.isSelected ? true : false;
        });
        const fileNames = serverFiles.map(x => x.fileName);
        this.spAttachmentLibraryService.deleteAttachments(this._listName, this._itemId, fileNames).then((data) => {
            this.loadExistingFiles();
        }).catch((error) => {
            console.log(error);
        });
    }

    getBase64(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = error => reject(error);
        });
    }

}
